<template>
  <!-- Panel lateral (solo lectura) con el detalle completo de un pago del
       reporte de cliente, agrupado por tema. Mismo patrón que el detalle de
       Bloqueados: hero con degradado cyan → lima pastel, métricas con ícono
       circular y secciones de clave/valor. -->
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    :width="ancho"
    class="detalle-pago"
    @input="$emit('input', $event)"
  >
    <div v-if="pago" class="dp-wrap">
      <div class="dp-hero">
        <div class="dp-hero-row">
          <div class="dp-hero-badge" :style="{ background: tono.bg }">
            <v-icon :color="tono.color" size="26">mdi-receipt-text-outline</v-icon>
          </div>
          <div class="dp-hero-info">
            <div class="dp-kicker">Pago #{{ pago.pago_id }}</div>
            <h2 class="dp-title">{{ f("importe_pagado") }}</h2>
            <div class="dp-sub">{{ pago.cliente }} · {{ pago.cuenta }}</div>
            <div class="dp-chips">
              <span class="dp-estado" :style="{ background: tono.bg, color: tono.color }">
                <v-icon size="13" :color="tono.color" class="mr-1">{{ tono.icono }}</v-icon>
                {{ labelEstado(pago.estado) }}
              </span>
              <span class="dp-chip-neutro">
                <v-icon size="13" color="#6b7280" class="mr-1">mdi-credit-card-outline</v-icon>
                {{ pago.medio_pago || "Sin medio de pago" }}
                <template v-if="pago.cuotas > 1"> · {{ pago.cuotas }} cuotas</template>
              </span>
            </div>
          </div>
          <v-btn icon class="dp-close" aria-label="Cerrar detalle" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="pago.referencia_externa" class="dp-id-chip mt-4">
          <v-icon size="16" color="#00B0EA" class="mr-2">mdi-pound</v-icon>
          <span class="dp-id-label">Referencia externa</span>
          <span class="dp-id-value ml-2">{{ pago.referencia_externa }}</span>
        </div>
      </div>

      <div class="dp-body">
        <!-- Recorrido del dinero: lo que pagó el pagador, lo que acreditó el
             proveedor y lo que se transfirió al cliente. -->
        <div class="dp-metricas">
          <div v-for="m in metricasPago" :key="m.label" class="dp-metrica">
            <div class="dp-metrica-icon" :style="{ background: m.bg }">
              <v-icon :color="m.color" size="20">{{ m.icono }}</v-icon>
            </div>
            <div class="dp-metrica-content">
              <div class="dp-metrica-label">{{ m.label }}</div>
              <div class="dp-metrica-value">{{ f(m.key) }}</div>
            </div>
          </div>
        </div>

        <section v-for="s in secciones" :key="s.titulo" class="dp-seccion">
          <div class="dp-seccion-titulo">
            <v-icon size="18" color="#00B0EA">{{ s.icono }}</v-icon>
            <span>{{ s.titulo }}</span>
          </div>
          <p v-if="s.vacio" class="dp-seccion-vacia">{{ s.vacio }}</p>
          <dl v-else class="dp-kv">
            <div v-for="c in s.campos" :key="c.key" :class="['dp-kv-item', { 'dp-kv-item--destacado': c.destacado }]">
              <dt>{{ c.label }}</dt>
              <dd>{{ c.valor !== undefined ? c.valor : f(c.key) }}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import {
  valorCampo,
  tonoEstado,
  labelEstado,
} from "../../views/Dasboard/Reportes/reporteFormato";

const hayValor = (v) => v !== null && v !== undefined && v !== 0 && v !== "" && v !== "-";

export default {
  name: "DetallePagoDrawer",
  props: {
    value: { type: Boolean, default: false },
    pago: { type: Object, default: null },
  },
  data() {
    return {
      labelEstado,
      metricas: [
        { key: "importe_pagado", label: "Pagado", icono: "mdi-cash", color: "#00B0EA", bg: "#E6F7FD" },
        {
          key: "importe_neto_cobrado",
          label: "Neto cobrado",
          icono: "mdi-bank-outline",
          color: "#7a8000",
          bg: "#F8FBD9",
        },
        {
          key: "importe_rendido",
          label: "Rendido",
          icono: "mdi-bank-transfer-in",
          color: "#009238",
          bg: "#E6F4EB",
        },
      ],
    };
  },
  computed: {
    ancho() {
      return this.$vuetify.breakpoint.smAndDown ? "100%" : 640;
    },
    // Neto cobrado: el que informa el operador o, en Prisma (que no lo informa
    // por operación), el neto interno calculado.
    metricasPago() {
      const sinNeto = this.pago && this.pago.sin_neto_operador;
      return this.metricas.map((m) =>
        m.key === "importe_neto_cobrado" && sinNeto
          ? { ...m, key: "importe_neto_cobrado_interno", label: "Neto cobrado (calculado)" }
          : m
      );
    },
    tono() {
      return tonoEstado(this.pago && this.pago.estado);
    },
    secciones() {
      const p = this.pago;
      if (!p) return [];
      const secciones = [];

      secciones.push({
        titulo: "Pago",
        icono: "mdi-receipt-text-outline",
        campos: [
          { key: "fecha_pago", label: "Fecha de pago" },
          { key: "tipo_pago", label: "Tipo de pago" },
          { key: "rubro", label: "Rubro" },
          { key: "importe_a_pagar", label: "Importe a pagar" },
          { key: "importe_pagado", label: "Importe pagado" },
          { key: "transaction_id", label: "Transaction ID" },
        ],
      });

      secciones.push({
        titulo: "Pagador",
        icono: "mdi-account-outline",
        campos: [
          { key: "pagador", label: "Nombre" },
          { key: "dni_pagador", label: "DNI" },
          { key: "correo_pagador", label: "Correo" },
        ],
      });

      // Fechas de estado: el reporte solo las informa en pagos revertidos.
      const fechasEstado = [];
      if (hayValor(p.autorizado)) fechasEstado.push({ key: "autorizado", label: "Autorizado el" });
      if (hayValor(p.revertido)) fechasEstado.push({ key: "revertido", label: "Revertido el" });

      secciones.push({
        titulo: "Liquidación y rendición",
        icono: "mdi-calendar-clock-outline",
        campos: [
          { key: "nro_establecimiento", label: "Nro. de establecimiento" },
          { key: "nro_liquidacion", label: "Nro. de liquidación" },
          { key: "fecha_presentacion", label: "Presentación" },
          { key: "fecha_acreditacion", label: "Acreditación" },
          { key: "fecha_rendicion", label: "Rendición" },
          { key: "referencia_bancaria", label: "Referencia bancaria" },
          ...fechasEstado,
        ],
      });

      // En Prisma el arancel sale del archivo de conciliación; si todavía no lo
      // informó, la sección lo dice en lugar de mostrar valores vacíos.
      const proveedor = [
        { key: "porcentaje_comision_proveedor", label: "Arancel" },
        { key: "comision_proveedor", label: "Comisión" },
        { key: "iva_comision_proveedor", label: "IVA comisión" },
        { key: "importe_minimo", label: "Importe mínimo" },
        { key: "importe_maximo", label: "Importe máximo" },
      ];
      if (p.arancel_pendiente) {
        proveedor.unshift({
          key: "_pendiente",
          label: "Arancel de Prisma",
          valor: "Pendiente de conciliación",
        });
      }
      secciones.push({ titulo: "Comisión del proveedor", icono: "mdi-handshake-outline", campos: proveedor });

      secciones.push({
        titulo: "Comisión Telco",
        icono: "mdi-percent-outline",
        campos: [
          { key: "comision_porcentaje_telco", label: "Porcentaje" },
          { key: "comision_telco", label: "Comisión" },
          { key: "iva_comision_telco", label: "IVA comisión" },
          { key: "alicuota_iva", label: "Alícuota IVA" },
          { key: "importe_minimo_telco", label: "Importe mínimo" },
          { key: "importe_maximo_telco", label: "Importe máximo" },
        ],
      });

      secciones.push({
        titulo: "Comisión total al cliente",
        icono: "mdi-sigma",
        campos: [
          { key: "comision_total", label: "Comisión total" },
          { key: "iva_comision_total", label: "IVA comisión total" },
        ],
      });

      const retenciones = [
        ["iibb", "IIBB"],
        ["iva", "IVA"],
        ["ganancias", "Ganancias"],
      ].filter(([g]) => hayValor(p["retencion_" + g]));
      secciones.push({
        titulo: "Retenciones",
        icono: "mdi-scale-balance",
        vacio: retenciones.length ? "" : "Este pago no tiene retenciones.",
        campos: retenciones.flatMap(([g, label]) => [
          { key: "porcentaje_retencion_" + g, label: "Alícuota " + label },
          { key: "retencion_" + g, label: "Retención " + label },
        ]),
      });

      // Costo financiero: solo en crédito en cuotas.
      const cft = ["importe_cft", "iva_cft", "costo_financiero", "iva_s_costo_financiero"].some((k) =>
        hayValor(p[k])
      );
      if (cft) {
        secciones.push({
          titulo: "Costo financiero",
          icono: "mdi-calculator-variant-outline",
          campos: [
            { key: "costo_financiero", label: "Costo financiero" },
            { key: "iva_s_costo_financiero", label: "IVA s/ costo financiero" },
            { key: "importe_cft", label: "CFT informado" },
            { key: "iva_cft", label: "IVA CFT" },
            { key: "alicuota_iva_cft", label: "Alícuota IVA CFT" },
            { key: "cft_coeficiente", label: "Coeficiente" },
          ],
        });
      }

      if (hayValor(p.monto_costo_servicio)) {
        secciones.push({
          titulo: "Costo de servicio",
          icono: "mdi-cog-outline",
          campos: [
            { key: "monto_costo_servicio", label: "Monto" },
            { key: "porcentaje_costo_servicio", label: "Porcentaje" },
          ],
        });
      }

      secciones.push({
        titulo: "Resultado",
        icono: "mdi-chart-timeline-variant",
        // Prisma no informa el neto por operación: sólo el interno.
        campos: [
          ...(p.sin_neto_operador
            ? []
            : [{ key: "importe_neto_cobrado", label: "Neto cobrado", destacado: true }]),
          {
            key: "importe_neto_cobrado_interno",
            label: "Neto cobrado interno",
            destacado: !!p.sin_neto_operador,
          },
          { key: "importe_rendido", label: "Rendido al cliente", destacado: true },
        ],
      });

      return secciones;
    },
  },
  methods: {
    f(campo) {
      return valorCampo(campo, this.pago);
    },
  },
};
</script>

<style scoped>
.dp-wrap {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  text-align: left;
  color: #2f2f2f;
}

/* ─── Hero ─────────────────────────────────────────────────────────── */
.dp-hero {
  background: linear-gradient(135deg, #e6f7fd 0%, #f8fbd9 100%);
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 176, 234, 0.12);
}
.dp-hero-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.dp-hero-badge {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(0, 176, 234, 0.12);
}
.dp-hero-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.dp-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #00b0ea;
}
.dp-title {
  font-family: "Plus Jakarta Sans", Montserrat, sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #2f2f2f;
  line-height: 1.2;
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.dp-sub {
  font-size: 13px;
  color: #4b5563;
  word-break: break-word;
}
.dp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.dp-estado,
.dp-chip-neutro {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  line-height: 1.2;
}
.dp-chip-neutro {
  background: rgba(255, 255, 255, 0.8);
  color: #4b5563;
  border: 1px solid #eef0f4;
  font-weight: 500;
}
.dp-close {
  margin: -6px -8px 0 0;
}
.dp-id-chip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: #e6f7fd;
  border: 1px solid #b8e8f8;
  border-radius: 8px;
  padding: 6px 10px;
  width: fit-content;
  max-width: 100%;
  font-size: 13px;
}
.dp-id-label {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.4px;
  color: #00b0ea;
  font-weight: 700;
}
.dp-id-value {
  font-weight: 700;
  color: #2f2f2f;
  word-break: break-all;
}

/* ─── Body ─────────────────────────────────────────────────────────── */
.dp-body {
  padding: 16px 24px 28px;
}
.dp-metricas {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 8px;
}
@media (max-width: 600px) {
  .dp-metricas {
    grid-template-columns: 1fr;
  }
}
.dp-metrica {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 12px;
  min-width: 0;
}
.dp-metrica-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dp-metrica-content {
  min-width: 0;
}
.dp-metrica-label {
  font-size: 12px;
  color: #6b7280;
}
.dp-metrica-value {
  font-family: "Plus Jakarta Sans", Montserrat, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #2f2f2f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

.dp-seccion {
  margin-top: 18px;
}
.dp-seccion-titulo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Plus Jakarta Sans", Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #2f2f2f;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid #eef0f4;
}
.dp-seccion-vacia {
  font-size: 13px;
  color: #6b7280;
  margin: 8px 0 0;
}
.dp-kv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 20px;
  margin: 0;
}
@media (max-width: 600px) {
  .dp-kv {
    grid-template-columns: 1fr;
  }
}
.dp-kv-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px dashed #eef0f4;
  min-width: 0;
}
.dp-kv-item dt {
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
}
.dp-kv-item dd {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #2f2f2f;
  text-align: right;
  word-break: break-word;
  font-variant-numeric: tabular-nums;
}
.dp-kv-item--destacado dd {
  color: #009238;
  font-weight: 700;
}
</style>
